import React, { useEffect, useRef } from 'react';
import * as THREE from 'three';


const HandInteractiveBackground = () => {

const containerRef = useRef(null);
const videoRef = useRef(null);
const handsRef = useRef(null);
const cameraRef = useRef(null);

useEffect(() => {

if (!containerRef.current || !videoRef.current) return;

const scene = new THREE.Scene();
scene.fog = new THREE.FogExp2(0x000000, 0.015);

const camera = new THREE.PerspectiveCamera(
60,
window.innerWidth / window.innerHeight,
0.1,
1000
);

camera.position.z = 50;

const renderer = new THREE.WebGLRenderer({
alpha: true,
antialias: true
});

renderer.setSize(window.innerWidth, window.innerHeight);
renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
containerRef.current.appendChild(renderer.domElement);

const particleCount = 6000;

const geometry = new THREE.BufferGeometry();

const positions = new Float32Array(particleCount * 3);
const colors = new Float32Array(particleCount * 3);
const sizes = new Float32Array(particleCount);

const shapes = [];

////////////////////////
/// GALAXY SHAPE
////////////////////////

const galaxyShape = new Float32Array(particleCount * 3);

for (let i = 0; i < particleCount; i++) {

const arm = i % 4;
const angle = (i / particleCount) * Math.PI * 12;
const r = 1.5 + (angle * 1.8);
const spread = (Math.random() - 0.5) * (15 / r);

galaxyShape[i * 3] = (r + spread) * Math.cos(angle + (arm * Math.PI / 2));
galaxyShape[i * 3 + 1] = (Math.random() - 0.5) * 6 * (1 / r);
galaxyShape[i * 3 + 2] = (r + spread) * Math.sin(angle + (arm * Math.PI / 2));

sizes[i] = Math.random() * 1.5 + 0.5;
}

shapes.push(galaxyShape);

////////////////////////
/// KNOT SHAPE
////////////////////////

const knotShape = new Float32Array(particleCount * 3);

for (let i = 0; i < particleCount; i++) {

const t = (i / particleCount) * Math.PI * 2;
const r1 = 14;
const r2 = 6;

knotShape[i * 3] = (r1 + r2 * Math.cos(3 * t)) * Math.cos(2 * t);
knotShape[i * 3 + 1] = (r1 + r2 * Math.cos(3 * t)) * Math.sin(2 * t);
knotShape[i * 3 + 2] = r2 * Math.sin(3 * t);
}

shapes.push(knotShape);

////////////////////////
/// DNA SHAPE
////////////////////////

const dnaShape = new Float32Array(particleCount * 3);

for (let i = 0; i < particleCount; i++) {

const strand = i % 2;
const t = (i / particleCount) * Math.PI * 15;
const y = (i / particleCount) * 50 - 25;
const r = 8;

dnaShape[i * 3] = r * Math.cos(t + strand * Math.PI);
dnaShape[i * 3 + 1] = y;
dnaShape[i * 3 + 2] = r * Math.sin(t + strand * Math.PI);
}

shapes.push(dnaShape);

////////////////////////
/// DIAMOND SHAPE
////////////////////////

const diamondShape = new Float32Array(particleCount * 3);

for (let i = 0; i < particleCount; i++) {

const layer = Math.random() * 30 - 15;

diamondShape[i * 3] = (Math.random() - 0.5) * (30 - Math.abs(layer));
diamondShape[i * 3 + 1] = layer;
diamondShape[i * 3 + 2] = (Math.random() - 0.5) * (30 - Math.abs(layer));
}

shapes.push(diamondShape);

for (let i = 0; i < particleCount * 3; i++) {
positions[i] = galaxyShape[i];
}

geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
geometry.setAttribute('color', new THREE.BufferAttribute(colors, 3));
geometry.setAttribute('size', new THREE.BufferAttribute(sizes, 1));

////////////////////////
/// PARTICLE TEXTURE
////////////////////////

const canvas = document.createElement('canvas');
canvas.width = 64;
canvas.height = 64;

const ctx = canvas.getContext('2d');

const gradient = ctx.createRadialGradient(32, 32, 0, 32, 32, 32);

gradient.addColorStop(0, 'rgba(255,255,255,1)');
gradient.addColorStop(0.3, 'rgba(255,255,255,0.8)');
gradient.addColorStop(1, 'rgba(255,255,255,0)');

ctx.fillStyle = gradient;
ctx.fillRect(0, 0, 64, 64);

const texture = new THREE.CanvasTexture(canvas);

const material = new THREE.PointsMaterial({
size: 1.2,
vertexColors: true,
transparent: true,
opacity: 0.9,
map: texture,
blending: THREE.AdditiveBlending,
depthWrite: false
});

const particleSystem = new THREE.Points(geometry, material);
scene.add(particleSystem);

////////////////////////
/// INTERACTION STATE
////////////////////////

let currentShapeIndex = 0;
let targetShape = shapes[0];

let lastPinchTime = 0;
let targetScale = 1;

let handX = 0;
let handY = 0;
let isHandVisible = false;

let animationFrameId;

////////////////////////
/// PARTICLE UPDATE
////////////////////////

const updateParticles = () => {

const posArray = particleSystem.geometry.attributes.position.array;
const colArray = particleSystem.geometry.attributes.color.array;

const time = Date.now() * 0.0003;

for (let i = 0; i < posArray.length; i += 3) {

posArray[i] += (targetShape[i] - posArray[i]) * 0.06;
posArray[i + 1] += (targetShape[i + 1] - posArray[i + 1]) * 0.06;
posArray[i + 2] += (targetShape[i + 2] - posArray[i + 2]) * 0.06;

}

const neonPalette = [
0x39ff14, // Neon Green
0xff6700, // Neon Orange
0xffff00, // Neon Yellow
0xff073a, // Neon Red
0x00ffff, // Cyan
0xff007f  // Neon Pink
];

const colorObj = new THREE.Color();

for (let i = 0; i < particleCount; i++) {

const color = neonPalette[i % neonPalette.length];
colorObj.setHex(color);

colArray[i * 3] = colorObj.r;
colArray[i * 3 + 1] = colorObj.g;
colArray[i * 3 + 2] = colorObj.b;
}

particleSystem.geometry.attributes.position.needsUpdate = true;
particleSystem.geometry.attributes.color.needsUpdate = true;

const baseScale = isHandVisible
? targetScale
: 1 + Math.sin(time * 2) * 0.05;

particleSystem.scale.lerp(
new THREE.Vector3(baseScale, baseScale, baseScale),
0.08
);

if (isHandVisible) {

particleSystem.rotation.y += (handX - particleSystem.rotation.y) * 0.08;
particleSystem.rotation.x += (handY - particleSystem.rotation.x) * 0.08;

} else {

particleSystem.rotation.y += 0.003;
particleSystem.rotation.x += 0.0015;
particleSystem.rotation.z += 0.0005;
}

renderer.render(scene, camera);

animationFrameId = requestAnimationFrame(updateParticles);
};

    ////////////////////////
    /// MEDIAPIPE LOADING
    ////////////////////////

    const loadMediaPipe = async () => {
      try {
        // Load MediaPipe Hands script dynamically
        if (!window.Hands) {
          await new Promise((resolve, reject) => {
            const script = document.createElement('script');
            script.src = 'https://cdn.jsdelivr.net/npm/@mediapipe/hands/hands.js';
            script.crossOrigin = 'anonymous';
            script.onload = resolve;
            script.onerror = () => reject(new Error('Failed to load MediaPipe Hands script'));
            document.head.appendChild(script);
          });
        }

        if (!window.Hands) throw new Error('Hands not found on window');

        const hands = new window.Hands({
          locateFile: (file) => `https://cdn.jsdelivr.net/npm/@mediapipe/hands/${file}`
        });

        handsRef.current = hands;

        hands.setOptions({
          maxNumHands: 1,
          modelComplexity: 1,
          minDetectionConfidence: 0.7,
          minTrackingConfidence: 0.7
        });

        hands.onResults((results) => {
          if (results.multiHandLandmarks && results.multiHandLandmarks.length > 0) {
            isHandVisible = true;
            const landmarks = results.multiHandLandmarks[0];
            const thumbTip = landmarks[4];
            const indexTip = landmarks[8];
            const wrist = landmarks[0];
            const middleTip = landmarks[12];
            const dx = thumbTip.x - indexTip.x;
            const dy = thumbTip.y - indexTip.y;
            const distance = Math.sqrt(dx * dx + dy * dy);
            const pdx = wrist.x - middleTip.x;
            const pdy = wrist.y - middleTip.y;
            const palmSize = Math.sqrt(pdx * pdx + pdy * pdy);
            targetScale = Math.max(0.5, Math.min(3.5, palmSize * 8));
            handX = (landmarks[9].x - 0.5) * Math.PI * 3;
            handY = (landmarks[9].y - 0.5) * Math.PI * 3;
            const now = Date.now();
            if (distance < 0.05 && now - lastPinchTime > 1000) {
              currentShapeIndex = (currentShapeIndex + 1) % shapes.length;
              targetShape = shapes[currentShapeIndex];
              lastPinchTime = now;
            }
          } else {
            isHandVisible = false;
          }
        });

        console.log("MediaPipe Hands initialized successfully");
        return hands;
      } catch (err) {
        console.error("MediaPipe initialization failed:", err);
        return null;
      }
    };

    ////////////////////////
    /// CAMERA & STARTUP
    ////////////////////////

    const startApp = async () => {
      // Start loading MediaPipe and Camera in parallel
      const handsPromise = loadMediaPipe();
      
      try {
        console.log("Requesting camera access...");
        const stream = await navigator.mediaDevices.getUserMedia({
          video: {
            width: { ideal: 480 },
            height: { ideal: 360 },
            facingMode: "user"
          }
        }).catch(async (e) => {
          console.warn("Retrying camera with minimal constraints...", e);
          return await navigator.mediaDevices.getUserMedia({ video: true });
        });

        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          cameraRef.current = stream;
          
          await new Promise((resolve) => {
            videoRef.current.onloadedmetadata = () => {
              videoRef.current.play()
                .then(() => {
                  console.log("Camera stream playing");
                  resolve();
                })
                .catch(e => {
                  console.warn("Video play failed (handled):", e);
                  resolve();
                });
            };
          });

          // Wait for hands to be ready, but only if they aren't yet
          const handsInstance = await handsPromise;

          const processManualFrame = async () => {
            if (videoRef.current && videoRef.current.readyState >= 2 && cameraRef.current === stream) {
              if (handsInstance) {
                try {
                  await handsInstance.send({ image: videoRef.current });
                } catch (e) {
                  // Silent fail to avoid loop crashing
                }
              }
              animationFrameId = requestAnimationFrame(processManualFrame);
            }
          };

          requestAnimationFrame(processManualFrame);
        }
      } catch (err) {
        console.error("Camera access failed or denied:", err);
        // Fallback to auto-animation is already handled by updateParticles
      }
    };

    startApp();
    updateParticles();

////////////////////////
/// RESIZE
////////////////////////

const handleResize = () => {

camera.aspect = window.innerWidth / window.innerHeight;
camera.updateProjectionMatrix();

renderer.setSize(window.innerWidth, window.innerHeight);
};

    const hostContainer = containerRef.current;
    window.addEventListener('resize', handleResize);

    return () => {
      cancelAnimationFrame(animationFrameId);
      window.removeEventListener('resize', handleResize);

      if (hostContainer && renderer.domElement) {
        hostContainer.removeChild(renderer.domElement);
      }

if (cameraRef.current) {
  if (cameraRef.current.getTracks) {
    cameraRef.current.getTracks().forEach(track => track.stop());
  } else if (cameraRef.current.stop) {
    cameraRef.current.stop();
  }
}

renderer.dispose();
geometry.dispose();
material.dispose();
texture.dispose();
};

}, []);

return (

<div
style={{
position: 'fixed',
top: 0,
left: 0,
width: '100vw',
height: '100vh',
zIndex: -1,
pointerEvents: 'none',
background:
'radial-gradient(circle at 50% 50%, #050505 0%, #000000 100%)'
}}
>

<video ref={videoRef} style={{ display: 'none' }} playsInline muted autoPlay />

<div ref={containerRef} style={{ width: '100%', height: '100%' }} />

</div>

);
};

export default HandInteractiveBackground;